import { useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { AlertNote } from "../../components/AlertNote";
import { Button } from "../../components/Button";
import { UnifiedModal } from "../../components/UnifiedModal";
import { BillingDetailsFields, useBillingDetailsForm } from "./BillingDetailsForm";

// Asks for the fiscal data a Stripe request was missing; `onSaved` retries that request.
export const BillingDetailsDialog = ({ onClose, onSaved }) => {
  const theme = useTheme();
  const form = useBillingDetailsForm();
  const [redirecting, setRedirecting] = useState(false);
  const busy = form.saving || redirecting;

  const handleSave = async () => {
    if (!(await form.submit())) return;
    setRedirecting(true);
    await onSaved();
  };

  const handleClose = () => {
    if (!busy) onClose();
  };

  return (
    <UnifiedModal
      open
      onClose={handleClose}
      maxWidth="sm"
      title="Datos de facturación"
      disableBackdropClose={busy}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="outline" onClick={handleClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={busy || form.isLoading || form.isError}>
            {redirecting ? "Redirigiendo…" : form.saving ? "Guardando…" : "Guardar y continuar"}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Antes de pagar necesitamos los datos fiscales de tu agencia. Aparecerán en todas tus facturas y podrás
          cambiarlos en Ajustes → Datos de facturación.
        </Typography>
        {form.isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : form.isError ? (
          <AlertNote
            variant="danger"
            action={
              <Button variant="outline" size="sm" onClick={() => form.refetch()}>
                Reintentar
              </Button>
            }
          >
            No se han podido cargar los datos de facturación.
          </AlertNote>
        ) : (
          <BillingDetailsFields form={form} disabled={redirecting} />
        )}
        {form.generalError && <AlertNote variant="danger">{form.generalError}</AlertNote>}
      </Stack>
    </UnifiedModal>
  );
};

BillingDetailsDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
